import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Globe, GraduationCap, Inbox, Loader2, Mail, MailOpen, Palette, Reply, Save, Send, Store,
    User, UtensilsCrossed,
} from "lucide-react";
import BackOffice from "@/components/account/BackOffice";
import { Btn, PageHead, Section, TONES, inputCls } from "@/components/account/Shell";
import { Cell, Filters, PillRow, Sheet, Tag, Txt } from "@/components/account/Sheet";
import api, { formatApiError, mediaUrl } from "@/lib/api";

const TONE = TONES.messages;
import { when } from "@/lib/when";
const day = (iso) => {
    try {
        const d = new Date(iso);
        const today = new Date();
        return d.toDateString() === today.toDateString()
            ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
            : d.toLocaleDateString([], { month: "short", day: "numeric" });
    } catch { return ""; }
};

const FOLDERS = [
    { key: "all", label: "Inbox", icon: Inbox },
    { key: "unread", label: "Unread", icon: Mail },
    { key: "customer", label: "Customer messages", icon: User },
    { key: "shops", label: "Shop-to-shop", icon: Store },
    { key: "artwork", label: "Artwork requests", icon: Palette },
    { key: "website", label: "Website requests", icon: Globe },
    { key: "classes", label: "Class signups", icon: GraduationCap },
    { key: "community", label: "Community", icon: UtensilsCrossed },
];

function rows(data) {
    const out = [];
    (data.messages || []).forEach((m) => out.push({
        id: `message-${m.id}`, rawId: m.id, kind: "message", replyKind: "message",
        folder: m.from_store_name ? "shops" : "customer",
        tag: m.from_store_name ? `Shop · ${m.from_store_name}` : "Customer",
        who: m.name || "Someone", email: m.email, phone: m.phone,
        subject: m.subject || "No subject", body: m.message, at: m.created_at,
        read: !!m.read, thread: m.thread, item: m,
    }));
    (data.artwork || []).forEach((a) => out.push({
        id: `artwork-${a.id}`, rawId: a.id, kind: "artwork", replyKind: "artwork", folder: "artwork",
        tag: `Artwork · ${a.status || "new"}`, who: a.name || "Someone", email: a.email, phone: a.phone,
        subject: a.service_label || "Artwork request", body: a.description, at: a.created_at,
        read: true, thread: a.thread, files: a.files, products: a.products,
        extra: a.date_needed ? `Needs it ${a.date_needed}` : "", item: a,
    }));
    (data.class_signups || []).forEach((s) => out.push({
        id: `class-${s.id}`, rawId: s.id, kind: "class", folder: "classes",
        tag: "Class signup", who: s.name || "Someone", email: s.email, phone: s.phone,
        subject: s.class_title, body: "", at: s.created_at, read: true, item: s,
    }));
    (data.leads || []).forEach((l) => out.push({
        id: `lead-${l.id}`, rawId: l.id, kind: "lead", folder: "website",
        tag: "Website request", who: l.name || "Someone", email: l.email, phone: l.phone,
        subject: "Wants a website", body: l.message, at: l.created_at, read: true, item: l,
    }));
    (data.community || []).forEach((t) => out.push({
        id: `community-${t.id}`, rawId: t.id, kind: "community", folder: "community",
        tag: t.mine ? "Community · your table" : "Community · you replied",
        who: t.author?.name || "Someone", email: t.author?.store_name || "", phone: "",
        subject: t.title, body: t.body, at: t.last_at, read: true,
        thread: (t.thread || []).map((r) => ({
            id: r.id, from: r.author?.store_name || r.author?.name || "Someone",
            at: r.created_at, message: r.body,
        })),
        extra: `${t.replies} repl${t.replies === 1 ? "y" : "ies"}`, item: t,
    }));
    return out.sort((a, b) => String(b.at || "").localeCompare(String(a.at || "")));
}

function ReplyBox({ kind, item, onSent }) {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);

    const send = async () => {
        if (!text.trim()) { toast.error("Write something first"); return; }
        setBusy(true);
        try {
            const { data } = await api.post(`/shop-inbox/reply/${kind}/${item.id}`, { message: text });
            toast[data.delivery === "sent" ? "success" : "error"](
                data.delivery === "sent" ? `Sent to ${data.to}` : `Saved, but the email is ${data.delivery}`);
            setText(""); setOpen(false); onSent();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    if (!open) return (
        <Btn onClick={() => setOpen(true)} testid={`reply-open-${item.id}`} tone="solid">
            <Reply size={12} /> Reply from here
        </Btn>
    );

    return (
        <div data-testid={`reply-box-${item.id}`}>
            <textarea className={inputCls} rows={4} value={text} onChange={(e) => setText(e.target.value)}
                placeholder={`Reply to ${item.name}…`} data-testid={`reply-text-${item.id}`} />
            <div className="mt-2 flex flex-wrap items-center gap-2">
                <Btn onClick={send} disabled={busy} testid={`reply-send-${item.id}`} tone="solid">
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send reply
                </Btn>
                <Btn onClick={() => setOpen(false)} testid={`reply-cancel-${item.id}`}>Cancel</Btn>
                <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-zinc-600">
                    goes out from 13thav with your shop as reply-to
                </p>
            </div>
        </div>
    );
}

function CommunityReply({ threadId, onSent }) {
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const send = async () => {
        if (!text.trim()) { toast.error("Write something first"); return; }
        setBusy(true);
        try {
            await api.post(`/community/threads/${threadId}/replies`, { body: text });
            toast.success("Posted in the community");
            setText(""); onSent();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <div>
            <textarea className={inputCls} rows={3} value={text} onChange={(e) => setText(e.target.value)}
                placeholder="Answer in the community…" data-testid={`community-reply-text-${threadId}`} />
            <div className="mt-2">
                <Btn onClick={send} disabled={busy} testid={`community-reply-send-${threadId}`} tone="solid">
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Post reply
                </Btn>
            </div>
        </div>
    );
}

function Reading({ row, onRead, onSent, onBack }) {    return (
        <Section tone={TONE} testid="inbox-reading" title={row.who} note={row.tag}
            right={row.kind === "message" && !row.read ? (
                <Btn onClick={() => onRead(row.rawId)} testid={`inbox-read-${row.rawId}`}>
                    <MailOpen size={11} /> mark read
                </Btn>
            ) : null}>
            <button onClick={onBack} data-testid="inbox-back"
                className="mb-3 inline-flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-600 hover:text-zinc-900">
                <ArrowLeft size={12} /> close
            </button>

            <p className="font-display text-lg font-black text-zinc-900">{row.subject}</p>
            <p className="mt-0.5 font-mono text-[11px] text-zinc-700">
                {row.who} · {row.email}{row.phone ? ` · ${row.phone}` : ""} · {when(row.at)}
            </p>
            {row.extra && <p className="mt-1 text-xs font-semibold text-zinc-800">{row.extra}</p>}

            {!!(row.products || []).length && (
                <p className="mt-3 flex flex-wrap gap-1.5">
                    {row.products.map((p) => (
                        <span key={p} className="rounded border border-black/15 bg-zinc-50 px-2 py-0.5 font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-800">{p}</span>
                    ))}
                </p>
            )}

            {row.body && (
                <p className="mt-4 whitespace-pre-line border-l-2 border-black/10 pl-4 text-sm text-zinc-900">{row.body}</p>
            )}

            {!!(row.files || []).length && (
                <div className="mt-4 flex flex-wrap gap-2">
                    {row.files.map((f, i) => (
                        <a key={i} href={mediaUrl(f.url)} target="_blank" rel="noreferrer"
                            className="rounded-full border border-black/15 bg-white px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-800 hover:border-zinc-900">
                            {f.filename || `file ${i + 1}`}
                        </a>
                    ))}
                </div>
            )}

            {!!(row.thread || []).length && (
                <div className="mt-5 space-y-3 border-t border-black/10 pt-4">
                    {row.thread.map((t) => (
                        <div key={t.id} data-testid={`thread-${t.id}`}>
                            <p className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-700">
                                {t.from} replied · {when(t.at)}
                            </p>
                            <p className="mt-1 whitespace-pre-line text-sm text-zinc-900">{t.message}</p>
                        </div>
                    ))}
                </div>
            )}

            {row.replyKind && (
                <div className="mt-5 border-t border-black/10 pt-4">
                    <ReplyBox kind={row.replyKind} item={row.item} onSent={onSent} />
                </div>
            )}
            {row.kind === "class" && (
                <p className="mt-5 border-t border-black/10 pt-4 text-xs text-zinc-700">
                    Class signups are a heads-up, not a thread — call or email them direct to confirm the seat.
                </p>
            )}
            {row.kind === "lead" && (
                <p className="mt-5 border-t border-black/10 pt-4 text-xs text-zinc-700">
                    Website request — reach out at <a href={`mailto:${row.email}`} className="font-semibold underline">{row.email}</a>
                    {row.phone ? <> or <a href={`tel:${row.phone}`} className="font-semibold underline">{row.phone}</a></> : ""} to book the call.
                </p>
            )}
            {row.kind === "community" && (
                <div className="mt-5 border-t border-black/10 pt-4" data-testid={`community-reply-${row.rawId}`}>
                    <CommunityReply threadId={row.rawId} onSent={onSent} />
                    <Link to="/community" className="mt-2 inline-block font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-800 hover:underline">
                        open it in the community →
                    </Link>
                </div>
            )}
        </Section>
    );
}

const COLS = [
    { key: "read", label: "", sub: "", w: "28px" },
    { key: "who", label: "From", sub: "click the row to read it", w: "minmax(170px,1.1fr)" },
    { key: "subject", label: "Subject", w: "minmax(220px,1.8fr)" },
    { key: "tag", label: "Kind", sub: "customer · shop · artwork · class · community", w: "minmax(150px,1fr)" },
    { key: "contact", label: "Contact", sub: "email · phone", w: "minmax(180px,1.2fr)" },
    { key: "when", label: "When", w: "96px" },
];

export default function StoreInbox() {
    const [data, setData] = useState(null);
    const [folder, setFolder] = useState("all");
    const [openId, setOpenId] = useState(null);
    const [inbox, setInbox] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        Promise.all([
            api.get("/shop-inbox/mine"),
            api.get("/store/community").catch(() => ({ data: { threads: [] } })),
        ]).then(([mine, comm]) => {
            setData({ ...mine.data, community: comm.data.threads || [] });
            setInbox(mine.data.store.notification_email || "");
        }).catch((e) => { setData({ error: formatApiError(e.response?.data?.detail) }); });
    }, []);
    useEffect(() => { load(); }, [load]);

    const all = useMemo(() => (data && !data.error ? rows(data) : []), [data]);
    const counts = useMemo(() => ({
        all: all.length,
        unread: all.filter((r) => !r.read).length,
        customer: all.filter((r) => r.folder === "customer").length,
        shops: all.filter((r) => r.folder === "shops").length,
        artwork: all.filter((r) => r.folder === "artwork").length,
        website: all.filter((r) => r.folder === "website").length,
        classes: all.filter((r) => r.folder === "classes").length,
        community: all.filter((r) => r.folder === "community").length,
    }), [all]);

    const saveInbox = async () => {
        setBusy(true);
        try {
            await api.put("/shop-inbox/mine/inbox", { notification_email: inbox });
            toast.success(inbox ? `Everything now goes to ${inbox}` : "Back to your login email");
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const markRead = async (id) => {
        try { await api.post(`/shop-inbox/messages/${id}/read`); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    if (!data) return <BackOffice><div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="animate-spin text-zinc-400" /></div></BackOffice>;

    if (data.error) return (
        <BackOffice>
            <div className="mx-auto max-w-2xl py-20 text-center" data-testid="inbox-locked">
                <Inbox size={34} className="mx-auto text-zinc-400" />
                <h1 className="mt-4 font-display text-2xl font-black text-zinc-900">An inbox comes with a space.</h1>
                <p className="mt-2 text-sm text-zinc-700">{data.error}</p>
                <Link to="/account/sell" className="mt-6 inline-block"><Btn tone="solid" testid="inbox-open-space">See the spaces</Btn></Link>
            </div>
        </BackOffice>
    );

    const shown = folder === "all" ? all
        : folder === "unread" ? all.filter((r) => !r.read)
            : all.filter((r) => r.folder === folder);
    const open = all.find((r) => r.id === openId) || null;
    const folderLabel = FOLDERS.find((f) => f.key === folder)?.label;

    const pick = (row) => {
        if (openId === row.id) { setOpenId(null); return; }
        setOpenId(row.id);
        if (row.kind === "message" && !row.read) markRead(row.rawId);
    };

    return (
        <BackOffice>
            <PageHead kicker="Messages" tone={TONE} title={`${data.store.name} inbox`} testid="inbox-head"
                sub="Everything customers and other shops send you, in one place. Each one is emailed to your notification address too. Click a row to read it right under the row."
                right={
                    <Link to="/account/orders" data-testid="inbox-to-orders"><Btn><Inbox size={11} /> Order inbox →</Btn></Link>
                } />

            <Filters testid="inbox-folders">
                <PillRow label="Show" testid="inbox-folder" value={folder} onPick={(k) => { setFolder(k); setOpenId(null); }}
                    choices={FOLDERS.map((f) => ({ key: f.key, label: f.label, count: counts[f.key] || 0 }))} />
            </Filters>

            <div className="mt-3">
                <Section title={`${folderLabel} · ${shown.length}`} pad={false} testid="inbox-list" note="newest first · bold rows are unread">
                    <Sheet cols={COLS} rows={shown} rowKey={(r) => r.id} testid="inbox-sheet" minWidth={900}
                        rowTestid={(r) => `inbox-row-${r.rawId}`} onRowClick={pick} active={(r) => r.id === openId}
                        rowCls={(r) => (r.read ? "" : "font-bold")}
                        expand={(r) => <Reading row={r} onRead={markRead} onSent={load} onBack={() => setOpenId(null)} />}
                        empty="Nothing in this folder yet. When it comes in, it lands here and in your email."
                        cells={(r) => ({
                            read: <Cell className="justify-center">{!r.read && <span className="h-2 w-2 bg-[#1a56db]" title="Unread" data-testid={`inbox-unread-${r.rawId}`} />}</Cell>,
                            who: <Cell><Txt bold={!r.read}>{r.who}</Txt></Cell>,
                            subject: <Cell><Txt bold={!r.read} title={r.subject}>{r.subject}</Txt></Cell>,
                            tag: <Cell><Tag cls="border-[#d0d0d0] bg-[#f5f5f5] text-[#1a1a1a]">{r.tag}</Tag></Cell>,
                            contact: <Cell><Txt sub>{[r.email, r.phone].filter(Boolean).join(" · ") || "—"}</Txt></Cell>,
                            when: <Cell><Txt sub>{day(r.at)}</Txt></Cell>,
                        })} />
                    {!shown.length && <p className="sr-only" data-testid="inbox-empty">Nothing in this folder yet.</p>}
                </Section>
            </div>

            <div className="mt-3">
                <Section title="Where your mail lands" tone={TONE} testid="inbox-settings"
                    note="Blank means your login email">
                    <div className="flex flex-wrap items-end gap-3">
                        <div className="min-w-[260px] flex-1">
                            <p className="mb-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-700">
                                Notification email
                            </p>
                            <input className={inputCls} placeholder={data.store.owner_email} value={inbox}
                                onChange={(e) => setInbox(e.target.value)} data-testid="inbox-email-input" />
                            <p className="mt-1.5 text-xs text-zinc-600">Blank = your login, {data.store.owner_email}</p>
                        </div>
                        <Btn onClick={saveInbox} disabled={busy} testid="inbox-email-save" tone="solid">
                            {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
                        </Btn>
                    </div>
                </Section>
            </div>
        </BackOffice>
    );
}
